import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { marked } from 'marked';
import { apiGet, apiPost } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type {
  InvitationDetailResponse,
  AcceptInvitationRequest,
  TermsAndConditionsResponse,
} from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportProblemLink from '../components/ReportProblemLink';
import './AcceptInvitePage.css';
import { fireConfetti } from '../utils/confetti';

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invitation, setInvitation] = useState<InvitationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // T&C
  const [terms, setTerms] = useState<TermsAndConditionsResponse | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);

  // Accept state
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || !token) return;
    fetched.current = true;

    (async () => {
      try {
        const [inv, tc] = await Promise.all([
          apiGet<InvitationDetailResponse>(`/api/invite/${token}`),
          apiGet<TermsAndConditionsResponse>('/api/terms/current'),
        ]);
        setInvitation(inv);
        setTerms(tc);
      } catch (err: any) {
        setError(err.message || 'Unable to load invitation.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  function savePendingInvite() {
    if (!token || !terms || !invitation) return;
    sessionStorage.setItem(
      'pendingInvite',
      JSON.stringify({ token, acceptedTermsId: terms.id, email: invitation.invitedEmail })
    );
  }

  function handleCreateAccount() {
    savePendingInvite();
    navigate('/register');
  }

  function handleSignIn() {
    savePendingInvite();
    navigate('/login');
  }

  async function handleAccept() {
    if (!token || !terms || !termsAccepted) return;
    setError('');
    setAccepting(true);

    try {
      const body: AcceptInvitationRequest = { acceptedTermsId: terms.id };
      await apiPost(`/api/invite/${token}/accept`, body);
      setAccepted(true);
      fireConfetti('medium');
      setTimeout(() => navigate('/'), 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  }

  function renderTermsHtml(): string {
    if (!terms) return '';
    return marked(terms.content) as string;
  }

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Invalid or not found
  if (!invitation) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Invitation</p>
          <div className="divider" />
          <AlertMessage type="error" message={error || 'This invitation link is not valid.'} />
          <div className="auth-links">
            <Link to="/login">Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // Already accepted
  if (invitation.isAccepted || accepted) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Invitation Accepted</p>
          <div className="divider" />
          <div className="invite-accepted-message">
            <p>You've joined the <strong>{invitation.vaultName}</strong>.</p>
            <p>Sign in to access your vault.</p>
          </div>
          <div className="auth-links">
            <Link to="/">Go to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (invitation.isExpired) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Invitation Expired</p>
          <div className="divider" />
          <div className="invite-expired-message">
            <p>
              This invitation to join <strong>{invitation.vaultName}</strong> has expired.
            </p>
            <p>
              Please ask {invitation.inviterName} to resend the invitation.
            </p>
          </div>
          <div className="auth-links">
            <Link to="/login">Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // Active invitation
  return (
    <div className="auth-page">
      <div className="auth-container invite-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">You've Been Invited</p>
        <div className="divider invite-desktop-hide" />

        <AlertMessage type="error" message={error} />

        <div className="invite-desktop-layout">
          <div className="invite-desktop-left">
            <div className="invite-details">
              <p>
                <strong>{invitation.inviterName}</strong> has invited you to join
              </p>
              <p className="invite-vault-name">{invitation.vaultName}</p>
              <p className="invite-role-label">
                as <span className="invite-role">{invitation.role}</span>
              </p>
              <p className="invite-email-label">Sent to: <strong>{invitation.maskedEmail}</strong></p>
            </div>
          </div>

          <div className="invite-desktop-right">
            {terms && (
              <div className="terms-section">
                <h3 className="terms-heading">Terms & Conditions (v{terms.version})</h3>
                <div
                  className="terms-content"
                  dangerouslySetInnerHTML={{ __html: renderTermsHtml() }}
                  onScroll={e => {
                    const el = e.currentTarget;
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20)
                      setTermsScrolled(true);
                  }}
                />
                {!termsScrolled && (
                  <p className="terms-scroll-hint">↓ Please scroll through the terms above to continue</p>
                )}
                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    disabled={!termsScrolled}
                    onChange={e => setTermsAccepted(e.target.checked)}
                  />
                  <span>I agree to the Terms & Conditions (v{terms.version})</span>
                </label>
              </div>
            )}

            {user ? (
              <button
                className="btn-primary invite-accept-btn"
                onClick={handleAccept}
                disabled={accepting || !termsAccepted}
              >
                {accepting ? <LoadingSpinner small /> : 'Accept Invitation'}
              </button>
            ) : invitation.inviteeHasAccount ? (
              <button
                className="btn-primary invite-accept-btn"
                onClick={handleSignIn}
                disabled={!termsAccepted}
              >
                Sign In to Accept
              </button>
            ) : (
              <button
                className="btn-primary invite-accept-btn"
                onClick={handleCreateAccount}
                disabled={!termsAccepted}
              >
                Create Account to Accept
              </button>
            )}
          </div>
        <p className="auth-site-link"><ReportProblemLink /></p>
        </div>
      </div>
    </div>
  );
}
